/* ==========================================================================
   Maintenance Schedule Calculator
   Self-contained. Loaded only on this calculator's page.
   No shared dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-pc-msc]');
  if (!root) return;

  /* Interval in miles, interval in months, and a short note. Figures are
     typical modern service intervals. A manufacturer schedule always wins. */
  var ITEMS = [
    { name: 'Engine oil and filter',      miles: 7500,   months: 12, sev: 0.6 },
    { name: 'Tire rotation',              miles: 6000,   months: 12, sev: 0.8 },
    { name: 'Engine air filter',          miles: 20000,  months: 36, sev: 0.5 },
    { name: 'Cabin air filter',           miles: 20000,  months: 24, sev: 0.6 },
    { name: 'Brake fluid',                miles: 45000,  months: 24, sev: 0.7 },
    { name: 'Fuel filter',                miles: 30000,  months: 48, sev: 0.8 },
    { name: 'Transmission fluid',         miles: 60000,  months: 72, sev: 0.6 },
    { name: 'Differential fluid',         miles: 50000,  months: 60, sev: 0.6 },
    { name: 'Coolant',                    miles: 100000, months: 60, sev: 0.8 },
    { name: 'Serpentine belt',            miles: 90000,  months: 84, sev: 0.9 },
    { name: 'Spark plugs',                miles: 0,      months: 120, sev: 0.9 },
    { name: 'Timing belt',                miles: 100000, months: 84, sev: 1.0 }
  ];

  var PLUG_MILES = { copper: 30000, platinum: 60000, iridium: 100000, diesel: 0 };

  /* ====================================================================
     compute(input) -> result

     PURE. No DOM, no network, no globals. All calculator logic lives here
     so it can be reasoned about and tested in isolation.
     ==================================================================== */
  function compute(input) {
    if (!isFinite(input.odo) || input.odo < 0) {
      return { ok: false, errors: ['Enter the current mileage.'] };
    }
    var age = isFinite(input.age) && input.age >= 0 ? input.age : 0;
    var perYear = isFinite(input.miles) && input.miles > 0 ? input.miles : 12000;
    var sevMult = input.severe ? 1 : 0;

    var due = [], soon = [], later = [];

    ITEMS.forEach(function (it) {
      var miles = it.miles;
      if (it.name === 'Spark plugs') {
        miles = PLUG_MILES[input.plugs];
        if (!miles) return;
      }
      if (it.name === 'Timing belt' && !input.timingBelt) return;
      if (sevMult) miles = Math.round(miles * it.sev);

      /* How far past the most recent multiple of the interval the vehicle is. */
      var since = input.odo % miles;
      var untilMiles = miles - since;
      var ageDue = age * 12 >= it.months;

      var entry = { name: it.name, interval: miles, until: untilMiles, months: it.months, ageDue: ageDue };
      if (untilMiles <= miles * 0.05 || since === 0) due.push(entry);
      else if (untilMiles <= 3000) soon.push(entry);
      else later.push(entry);
    });

    /* Time based items the mileage may never reach. */
    var timeFlags = [];
    if (age >= 2) timeFlags.push('Brake fluid is time based. At ' + num(age, 0) + ' years it is due regardless of mileage.');
    if (age >= 5) timeFlags.push('Coolant is time based too. Five years is the usual limit.');
    if (age >= 6) timeFlags.push('Tires age out at about six years even with tread remaining. Check the DOT date codes.');

    var status;
    if (due.length >= 3) {
      status = { key: 'now', label: due.length + ' items due',
                 note: 'Several services have come round together. Doing them in one visit saves labour.' };
    } else if (due.length) {
      status = { key: 'soon', label: due.length + ' item' + (due.length > 1 ? 's' : '') + ' due',
                 note: due.map(function (d) { return d.name; }).join(', ') + '.' };
    } else if (soon.length) {
      status = { key: 'monitor', label: 'Nothing due yet',
                 note: soon.length + ' item' + (soon.length > 1 ? 's' : '') + ' coming up within 3,000 miles.' };
    } else {
      status = { key: 'good', label: 'All up to date',
                 note: 'Nothing due on mileage. Check the time based items below.' };
    }

    var rows = [];
    if (due.length) {
      rows.push({ head: 'Due now' });
      due.forEach(function (d) { rows.push({ label: d.name, value: 'every ' + comma(d.interval) + ' mi' }); });
    }
    if (soon.length) {
      rows.push({ head: 'Within 3,000 miles' });
      soon.forEach(function (d) { rows.push({ label: d.name, value: 'in ' + comma(Math.round(d.until)) + ' mi' }); });
    }
    if (later.length) {
      rows.push({ head: 'Later' });
      later.sort(function (a, b) { return a.until - b.until; });
      later.forEach(function (d) { rows.push({ label: d.name, value: 'in ' + comma(Math.round(d.until)) + ' mi' }); });
    }
    if (timeFlags.length) {
      rows.push({ head: 'Time based, regardless of mileage' });
      timeFlags.forEach(function (t, i) { rows.push({ label: 'Note ' + (i + 1), value: t }); });
    }

    var nextItem = due.length ? due[0] : (soon.concat(later).sort(function (a, b) { return a.until - b.until; })[0]);

    return {
      ok: true, errors: [], warnings: input.severe
        ? ['Severe service intervals are applied. Short trips, towing, dust and temperature extremes all shorten these figures, and most city driving qualifies.']
        : [],
      status: status,
      figures: [
        { label: 'Due now', value: due.length ? String(due.length) + ' item' + (due.length > 1 ? 's' : '') : 'Nothing',
          sub: due.length ? due.map(function (d) { return d.name; }).join(', ') + '.' : 'Nothing has come round on mileage.' },
        { label: 'Next service', value: nextItem ? nextItem.name : 'None',
          sub: nextItem ? (nextItem.until <= 0 ? 'Due now.' : 'In about ' + comma(Math.round(nextItem.until)) + ' miles, roughly ' + num((nextItem.until / perYear) * 12, 0) + ' months.') : '' }
      ],
      rows: rows
    };
  }

  /* ====================================================================
     Formatting
     ==================================================================== */
  function comma(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function num(n, dp) {
    if (!isFinite(n)) return '-';
    var v = Number(n).toFixed(dp == null ? 2 : dp);
    if (dp !== 0 && v.indexOf('.') > -1) v = v.replace(/\.?0+$/, '');
    var parts = v.split('.');
    return comma(parts[0]) + (parts[1] ? '.' + parts[1] : '');
  }

  function signed(n, dp) {
    if (!isFinite(n)) return '-';
    return (n > 0 ? '+' : '') + num(n, dp);
  }

  /* ====================================================================
     DOM wiring
     ==================================================================== */
  var FIELDS = [
      {
          "name": "odo",
          "key": "o",
          "type": "number"
      },
      {
          "name": "age",
          "key": "a",
          "type": "number"
      },
      {
          "name": "miles",
          "key": "m",
          "type": "number"
      },
      {
          "name": "plugs",
          "key": "p",
          "type": "select"
      },
      {
          "name": "timingBelt",
          "key": "tb",
          "type": "checkbox"
      },
      {
          "name": "severe",
          "key": "sv",
          "type": "checkbox"
      }
  ];

  var els = {
    error:    root.querySelector('[data-r-error]'),
    status:   root.querySelector('[data-r-status]'),
    statusLbl:root.querySelector('[data-r-status-label]'),
    statusVal:root.querySelector('[data-r-status-value]'),
    statusNote:root.querySelector('[data-r-status-note]'),
    figures:  root.querySelector('[data-r-figures]'),
    rows:     root.querySelector('[data-r-rows]'),
    warnings: root.querySelector('[data-r-warnings]')
  };

  var STATUS_CLASSES = ['is-good','is-monitor','is-soon','is-now','is-critical'];

  function field(name) { return root.querySelector('[data-f="' + name + '"]'); }

  function readInput() {
    var o = {};
    FIELDS.forEach(function (f) {
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') o[f.name] = el.checked;
      else if (f.type === 'number') o[f.name] = parseFloat(el.value);
      else o[f.name] = el.value;
    });
    return o;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }

  function renderFigures(figs) {
    els.figures.innerHTML = (figs || []).map(function (f) {
      return '<div class="pc-msc__figure">'
        + '<div class="pc-msc__figure-label">' + esc(f.label) + '</div>'
        + '<div class="pc-msc__figure-value' + (f.muted ? ' is-muted' : '') + '">'
        + esc(f.value) + '</div>'
        + (f.sub ? '<div class="pc-msc__figure-sub">' + esc(f.sub) + '</div>' : '')
        + '</div>';
    }).join('');
  }

  function renderRows(rows) {
    if (!els.rows) return;
    els.rows.innerHTML = (rows || []).map(function (r) {
      if (r.head) {
        return '<div class="pc-msc__row pc-msc__rows-head">' + esc(r.head) + '</div>';
      }
      return '<div class="pc-msc__row">'
        + '<span class="pc-msc__row-label">' + esc(r.label) + '</span>'
        + '<span class="pc-msc__row-value">' + esc(r.value) + '</span>'
        + '</div>';
    }).join('');
  }

  function clearResult(message) {
    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      els.statusVal.textContent  = 'Waiting for your numbers';
      els.statusNote.textContent = message || '';
    }
    els.figures.innerHTML = '';
    if (els.rows) els.rows.innerHTML = '';
    if (els.warnings) els.warnings.style.display = 'none';
  }

  function render() {
    var input  = readInput();
    var result = compute(input);

    if (!result.ok) {
      els.error.textContent = result.errors[0];
      els.error.classList.add('is-shown');
      clearResult(result.errors[0]);
      return;
    }
    els.error.classList.remove('is-shown');

    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      if (result.status) {
        els.status.classList.add('is-' + result.status.key);
        els.statusVal.textContent  = result.status.label;
        els.statusNote.textContent = result.status.note || '';
      } else {
        els.statusVal.textContent  = '';
        els.statusNote.textContent = '';
      }
    }

    renderFigures(result.figures);
    renderRows(result.rows);

    if (els.warnings) {
      if (result.warnings && result.warnings.length) {
        els.warnings.textContent = result.warnings[0];
        els.warnings.style.display = '';
      } else {
        els.warnings.style.display = 'none';
      }
    }

    writePermalink(input);
  }

  /* ====================================================================
     Shareable result permalinks
     ==================================================================== */
  function writePermalink(input) {
    if (!window.history || !window.history.replaceState) return;
    var q = FIELDS.map(function (f) {
      var v = input[f.name];
      if (f.type === 'checkbox') return v ? f.key + '=1' : '';
      if (v == null || v !== v || v === '') return '';
      return f.key + '=' + encodeURIComponent(v);
    }).filter(Boolean).join('&');
    try {
      window.history.replaceState(null, '', window.location.pathname + (q ? '?' + q : ''));
    } catch (e) {}
  }

  function applyPermalink() {
    if (!window.URLSearchParams) return;
    var p = new URLSearchParams(window.location.search);
    var any = false;
    FIELDS.forEach(function (f) {
      if (!p.has(f.key)) return;
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') el.checked = p.get(f.key) === '1';
      else el.value = p.get(f.key);
      any = true;
    });
    return any;
  }

  /* ====================================================================
     Init
     ==================================================================== */
  ['input', 'change'].forEach(function (evt) {
    root.addEventListener(evt, function (e) {
      if (e.target.closest('[data-r-form]')) render();
    });
  });

  applyPermalink();
  render();

  /* Exposed for testing and debugging only. */
  window.PC_MSC = { compute: compute };
})();
